import classNames from 'classnames';
import type { ReactNode } from 'react';
import type { CSSProperties } from 'styled-components';
import styled, { keyframes } from 'styled-components';

import { Locker } from '#src/atoms/icons/Locker.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { unitMapping, type Unit } from '#src/lib/style.ts';
import type { HexColor } from '#src/style/types.ts';

const Root = styled(Horizontal)`
  width: 100%;

  --segment-fill-color: var(--color);
  --glow-color: var(--color);

  &.default {
    --segments-height: var(--unit);
  }

  &.oblique {
    --segments-oblique-angle: 3px;
    --segments-height: var(--double-unit);

    &.split {
      --segments-height: var(--intermediate-unit);
      &.withReward {
        --segments-height: var(--double-and-a-half-unit);
      }
    }
  }
`;

const SegmentsWrapper = styled(Horizontal)`
  gap: var(--segments-gap);
  flex: 1;

  & > * {
    flex: 1;
  }

  .oblique & {
    position: relative;
    isolation: isolate;
    height: var(--segments-height);
  }
`;

const Segment = styled(Horizontal).attrs({ gap: 0, center: true })`
  --segment-height: var(--unit);
  height: var(--segment-height);

  &.hide {
    display: none;
  }

  .iridescent & {
    --segment-fill-color: linear-gradient(
      84deg,
      #f8d3da 0%,
      #b3a9f4 28.32%,
      #fbe9fb 54.01%,
      #4f94fd 100%
    );
    --glow-color: #4f94fd;
  }

  .default & {
    position: relative;
    overflow: hidden;
    isolation: isolate;

    border-radius: 2px;
    background-color: var(--c-nd-150);

    &.full {
      inset: 0;
      background: var(--segment-fill-color);
      box-shadow: 0px 0px 10px 0px rgba(255, 255, 255, 0.65) inset;
    }

    .glow &.full {
      filter: drop-shadow(0px 1px 7px var(--glow-color));
    }

    &.negativePartial {
      --partial-opacity: 0.25;
      --partial-background:
        linear-gradient(to right, var(--c-nl-50) 30%, var(--c-nl-500) 100%),
        var(--segment-fill-color);
    }

    &.partial {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: var(--default-segment-width);
        background: var(--partial-background, var(--segment-fill-color));

        opacity: var(--partial-opacity, 0.5);
      }
    }
  }

  .oblique.split & {
    left: var(--oblique-segment-offset);
  }

  .oblique & {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: calc(100% - var(--oblique-segment-width));
    height: 100%;
    z-index: var(--oblique-segment-z-index);
    font-size: calc(var(--segment-height) * 0.9);

    clip-path: polygon(
      var(--segments-oblique-angle) 0%,
      100% 0%,
      calc(100% - var(--segments-oblique-angle)) 100%,
      0% 100%
    );

    &.full {
      --partial-opacity: 1;
    }
    &.empty {
      background: linear-gradient(
        270deg,
        var(--c-nd-50-opaque) 1.98%,
        var(--c-nd-150-opaque) 85.3%
      );
    }

    &.negativePartial {
      --partial-opacity: 0.25;
      --partial-background:
        linear-gradient(to right, var(--c-nl-50) 30%, var(--c-nl-500) 100%),
        var(--segment-fill-color);
    }

    &.partial:not(.empty) {
      background: var(
        --partial-background,
        linear-gradient(
          270deg,
          var(--c-nd-50-opaque) 1.98%,
          var(--c-nd-150-opaque) 85.3%
        )
      );
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: var(--default-segment-width);
      background: var(--partial-background, var(--segment-fill-color));

      clip-path: polygon(
        var(--segments-oblique-angle) 0%,
        100% 0%,
        calc(100% - var(--segments-oblique-angle)) 100%,
        0% 100%
      );
      opacity: var(--partial-opacity, 0.5);
      transition: width 0.3s ease-in-out;
    }
  }
`;

const rewardReached = keyframes`
30% {
    transform: scale(1.2);
}
40%, 60% {
    transform: rotate(-20deg) scale(1.2);
}
50% {
    transform: rotate(20deg) scale(1.2);
}
70% {
    transform: rotate(0deg) scale(1.2);
}
100% {
    transform: scale(1);
}
`;

const PositionnedReward = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  cursor: pointer;
  top: 50%;
  transform: translateY(-50%);
  right: -1px;
  z-index: 1;
  width: var(--triple-unit);
  height: var(--triple-unit);
  background-color: var(--c-nd-150);
  transition: background-color 0.3s ease-in-out;
  &.full {
    background: var(--c-gradient-discount);
    color: var(--c-black);
    svg {
      animation: ${rewardReached} 1s ease-in-out;
    }
  }

  .oblique & {
    clip-path: polygon(
      var(--segments-oblique-angle) 0%,
      100% 0%,
      calc(100% - var(--segments-oblique-angle)) 100%,
      0% 100%
    );
  }
`;

type SegmentData = {
  isFull: boolean;
  isPartial: boolean;
  isEmpty: boolean;
  isNegative: boolean;
  isTarget: boolean;
  defaultSegmentWidth?: string;
  obliqueSegmentWidth: string;
  obliqueSegmentOffset: string;
  obliqueSegmentZIndex: number;
  hide?: boolean;
  reward?: ReactNode;
};
type Props = {
  thresholds: { score: number; reward?: ReactNode }[];
  currentScore: number;
  previewScore?: number;
  targetScore?: number;
  color: HexColor | 'iridescent';
  label?: ReactNode;
  glow?: boolean;
  variant?: 'oblique' | 'default';
  split?: boolean;
  gap?: Unit;
  logarithmic?: boolean;
};

export const SplitProgressBar = ({
  thresholds,
  currentScore: referenceScore,
  previewScore: optionalScore,
  targetScore,
  color,
  label,
  glow,
  variant = 'default',
  split = true,
  gap,
  logarithmic = false,
}: Props) => {
  if (!thresholds.length) {
    return null;
  }

  const previewScore = Math.max(
    optionalScore ?? referenceScore,
    referenceScore
  );
  const validScore = Math.min(optionalScore ?? referenceScore, referenceScore);

  const progressBarScoreMax = thresholds[thresholds.length - 1].score;

  // Helper function to transform scores based on logarithmic option
  const transformScore = (score: number): number => {
    if (!logarithmic || score <= 0) return score;
    return Math.log1p(score / 5000);
  };

  const transformedMax = transformScore(progressBarScoreMax);
  const progressBarScoreUnit = transformedMax > 0 ? 1 / transformedMax : 0;

  // Compute all segments
  const segments: SegmentData[] = thresholds.map((threshold, index) => {
    const fullBar = validScore >= threshold.score;
    const previousBonusThresholdValue =
      (index > 0 && thresholds.at(index - 1)?.score) || 0;
    const partialBar = !fullBar && previewScore >= previousBonusThresholdValue;
    const defaultSegmentWidth = (() => {
      if (fullBar) {
        return 1;
      }
      if (partialBar) {
        const transformedPreview = transformScore(previewScore);
        const transformedPrevious = transformScore(previousBonusThresholdValue);
        const transformedThreshold = transformScore(threshold.score);
        const transformedDiff = transformedThreshold - transformedPrevious;

        if (transformedDiff <= 0) return 1;

        return Math.min(
          1,
          (transformedPreview - transformedPrevious) / transformedDiff
        );
      }
      return undefined;
    })();

    const fullLayer = 2;
    const partialLayer = 1;
    const emptyLayer = 0;

    const isPartial = !!partialBar;
    const isFull = fullBar && !isPartial;
    const isEmpty = !fullBar && !defaultSegmentWidth;

    // in `oblique` variant, we want to display the full segment first, then the partial one, then the empty one
    let obliqueSegmentZIndex = thresholds.length;
    if (isFull) {
      obliqueSegmentZIndex = obliqueSegmentZIndex * fullLayer + index;
    } else if (isEmpty) {
      obliqueSegmentZIndex = obliqueSegmentZIndex * emptyLayer + index;
    } else if (isPartial) {
      obliqueSegmentZIndex = obliqueSegmentZIndex * partialLayer + index;
    }

    const transformedThreshold = transformScore(threshold.score);
    const transformedPrevious = transformScore(previousBonusThresholdValue);

    return {
      isFull,
      isPartial,
      isEmpty,
      isNegative: (optionalScore ?? referenceScore) < referenceScore,
      isTarget: targetScore === threshold.score,
      defaultSegmentWidth: `calc(${defaultSegmentWidth} * 100%)`,
      obliqueSegmentZIndex,
      obliqueSegmentOffset: `calc(${index ? 'var(--segments-gap)' : '0px'} + (${transformedPrevious * progressBarScoreUnit} * 100%))`,
      obliqueSegmentWidth: `calc(${transformedThreshold * progressBarScoreUnit} * 100%)`,
      reward: threshold.reward,
    };
  });

  const formattedSegments = segments.map((segment, segmentIndex) => {
    const previousSegment = segments[segmentIndex + 1] || segment;

    // in `oblique` variant, we only display the last full segment, the last partial one, and the last empty one
    if (
      !split &&
      ((previousSegment?.isPartial && !segment.isPartial) ||
        (previousSegment?.isFull && !segment.isFull) ||
        (previousSegment?.isEmpty && !segment.isEmpty) ||
        segmentIndex === segments.length - 1)
    ) {
      return {
        ...segment,
        hide: false,
      };
    }
    return { ...segment, hide: !split };
  });

  const gapValue = (() => {
    if (gap) return unitMapping[gap];
    if (variant === 'oblique') return unitMapping[0];
    return '1px';
  })();

  return (
    <Root
      style={{ '--color': color, '--segments-gap': gapValue } as CSSProperties}
      className={classNames(variant, {
        split,
        glow,
        iridescent: color === 'iridescent',
        withReward: !!segments.some(segment => !!segment.reward),
      })}
      role="progressbar"
    >
      <SegmentsWrapper>
        {formattedSegments.map((segment, index) => (
          <Segment
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={classNames({
              full: segment.isFull,
              partial: segment.isPartial,
              empty: segment.isEmpty,
              negativePartial: segment.isNegative,
              hide: segment.hide,
            })}
            style={
              {
                '--default-segment-width': segment.defaultSegmentWidth,
                '--oblique-segment-width': segment.obliqueSegmentWidth,
                '--oblique-segment-offset': segment.obliqueSegmentOffset,
                '--oblique-segment-z-index': segment.obliqueSegmentZIndex,
              } as CSSProperties
            }
          >
            {!!segment.isTarget && !segment.isFull && (
              <Locker width={8} height={8} />
            )}
            {!!segment?.reward && (
              <PositionnedReward
                className={classNames({
                  full: segment.isFull,
                })}
              >
                {segment.reward}
              </PositionnedReward>
            )}
          </Segment>
        ))}
      </SegmentsWrapper>
      {label}
    </Root>
  );
};
